<script>
import moment from 'moment/moment';
import {useTabStore} from '@store/tabStore';

/* COMPOSANTS */
import AkAllFilter from '@components/v2/filter/AkAllFilter';
import AkAutocompleteFilter from '@components/v2/filter/AkAutocompleteFilter';
import AkListFilter from '@components/v2/filter/AkListFilter';
import AkPlanning from '@components/general/AkPlanning';
import AkPlanningYear from '@components/general/AkPlanningYear';
import GaListView from '@components/v2/layout/GaListView';
import GaView from '@components/v2/layout/GaView';
import CreateOwnerReservationSidebar from '@views/reservation/planning/CreateOwnerReservationSidebar';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2';

/* MIXINS */
import accommodationAutocompleteMixin from '@mixins/autocomplete/accommodationAutocompleteMixin';
import accommodationTypeConst from '@mixins/const/accommodationTypeConst';
import companyMixin from '@mixins/companyMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';
import ownerAutocompleteMixin from '@mixins/autocomplete/ownerAutocompleteMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import randomRef from '@mixins/randomRef';
import residenceTypeConst from '@mixins/const/residenceTypeConst';
import sidebarMixin from '@mixins/sidebarMixin';
import smilyStatusConst from '@mixins/const/smilyStatusConst';
import utilsMixin from '@mixins/utilsMixin';

/* SERVICES */
import accommodationService from '@services/accommodationService';
import reservationPlanningService from '@services/reservationPlanningService';
import GaLayoutEmptyTab from '@components/v2/general/GaLayoutEmptyTab.vue';
import mainTabMixin from '@mixins/mainTabMixin';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';

export default {
  components: {
    AkButtonValidation,
    GaLayoutEmptyTab,
    AkAllFilter,
    AkAutocompleteFilter,
    AkListFilter,
    GaListView,
    AkPlanningYear,
    GaView,
    AkPlanning,
    CreateOwnerReservationSidebar,
    AkButtonActionV2,
  },
  mixins: [
    accommodationTypeConst,
    loaderMixin,
    messageMixin,
    permissionsMixin,
    randomRef,
    residenceTypeConst,
    sidebarMixin,
    smilyStatusConst,
    utilsMixin,
    companyMixin,
    ownerAutocompleteMixin,
    accommodationAutocompleteMixin,
    mainTabMixin,
  ],
  head() {
    return {
      title: 'reservation.planning',
    };
  },
  data() {
    return {
      datePlanning: moment(),
      companies: [],
      canHideLoader: true,
      loader: undefined,
      width: 0,
      filter: {date: moment().toDate()},
      planning: [],
      displayYear: false,
      accommodations: [],
      accommodation: {},
      durationSelectedId: 0,
    };
  },
  mounted() {
    this.durationSelectedId = useTabStore().getTabActive('ReservationPlanning', 0);
    this.refresh();
  },
  computed: {
    canDisplayYearly() {
      return (
        this.isOwner() ||
        ((this.planning?.length > 0 || typeof this.planning === 'object') &&
          this.filter.accommodationId != undefined &&
          this.filter.ownerId == undefined &&
          !this.filter.companyIds?.length &&
          !this.filter.clusters?.length &&
          !this.filter.typologies?.length &&
          !this.filter.physicalReception &&
          !this.filter.laundry)
      );
    },
    getDurationsOptions() {
      return [
        {label: '2 semaines', value: 15},
        {label: '30 jours', value: 30},
        ...(this.canDisplayYearly ? [{label: 'Annuel', value: 0}] : []),
      ];
    },
    hasSingleAccommodation() {
      return this.accommodations.length === 1;
    },
    singleAccommodationId() {
      return this.accommodations[0].id;
    },
    nbDays() {
      const option = this.getDurationsOptions[this.durationSelectedId];
      return option?.value || 15;
    },
  },
  methods: {
    refresh() {
      if (this.isOwner() || this.isHousekeeper()) {
        this.fetchAccommodationsAndLoadPlanning();
      } else if ((this.isGas() || this.isGav() || this.isPartenaire()) && this.$route.query.accommodationId) {
        this.filter.accommodationId = this.$route.query.accommodationId;
        this.selectDuration(2, true);
      } else if (this.isGav() || this.isPartenaire()) {
        this.loadPlanning();
      } else if (this.isGas()) {
        this.today();
      }
    },
    reloadPage() {
      if (this.filter.accommodationId) this.$route.query.accommodationId = this.filter.accommodationId; //rustine pour fix le rechargement de la page si l'on vient d'une fiche logement
      this.refresh();
    },
    fetchAccommodationsAndLoadPlanning() {
      const params = {
        ...(this.isOwner() && {userId: this.myUserId()}),
        enabled: true,
        sort: ['name:ASC'],
      };
      accommodationService.accommodationThumbnails(params).then((data) => {
        this.accommodations = data;
        if (this.isOwner()) {
          this.initAccommodationFilter();
          this.selectDuration(2, true);
        } else if (this.$route.query.accommodationId) {
          this.filter.accommodationId = parseInt(this.$route.query.accommodationId);
          this.selectDuration(2, true);
        } else {
          this.selectDuration(0, true);
        }
      });
    },
    initAccommodationFilter() {
      if (this.$route.query.accommodationId) {
        this.filter.accommodationId = parseInt(this.$route.query.accommodationId);
      } else if (this.accommodations?.length > 0) {
        this.filter.accommodationId = this.accommodations[0].id;
      }
    },
    loadPlanning() {
      this.showListLoader();
      reservationPlanningService
        .planning(this.filter)
        .then((data) => {
          this.planning = data;
          this.removeDuplicate();
          this.canHideLoader = true;
        })
        .finally(() => {
          this.hideCustomLoader();
        });
    },
    deleteReservation(success) {
      if (success) {
        this.success(this.$t('unavailability.deleted'));
        this.refresh();
      } else {
        this.addError(this.$t('unavailability.impossible_to_cancel'));
      }
    },
    resetFilter() {
      this.filter = {date: moment().toDate()};
      this.openFilterPanel();
    },
    selectDuration(index, forceSelect = false) {
      if (this.canHideLoader === false || (index === this.durationSelectedId && !forceSelect)) {
        return;
      }
      this.showListLoader();

      setTimeout(() => {
        if (index !== 2) useTabStore().setTabActive('ReservationPlanning', index);
        this.durationSelectedId = index;
        if (this.getDurationsOptions.length > index && !this.getDurationsOptions[index].value) {
          this.filter.dateYearly = new Date();
          this.getPlanningYearly();
        } else if (index !== 2) {
          this.getPlanning();
        }
      }, 0);
    },
    showListLoader() {
      this.$nextTick(() => {
        this.showLoader(this.$refs.listView);
      });
    },
    navigate(amount, unit) {
      this.showListLoader();
      this.filter.date = moment(this.datePlanning).add(amount, unit).toDate();
      if (unit === 'y') this.getPlanningYearly();
      else this.getPlanning();
    },
    nextDays() {
      this.navigate(10, 'd');
    },
    prevDays() {
      this.navigate(-10, 'd');
    },
    nextYear() {
      this.navigate(1, 'y');
    },
    prevYear() {
      this.navigate(-1, 'y');
    },
    today() {
      this.filter.date = moment().toDate();
      this.showListLoader();
      if (this.displayYear) this.getPlanningYearly();
      else this.getPlanning();
    },
    hideCustomLoader() {
      if (this.canHideLoader) {
        this.hideLoader();
      }
    },
    search() {
      if (this.canHideLoader === false) return;
      if ((this.displayYear && this.canDisplayYearly) || this.isOwner()) {
        if (this.durationSelectedId === 2) {
          if (!this.canDisplayYearly) this.durationSelectedId = 0;
        }
        this.selectDuration(this.durationSelectedId, true);
      } else {
        this.selectDuration(this.durationSelectedId === 2 ? 0 : this.durationSelectedId, true);
      }
    },

    // PLANNING

    getPlanning() {
      this.canHideLoader = false;
      if (!this.durationSelectedId) this.durationSelectedId = 0;
      this.filter.dateYearly = undefined;
      reservationPlanningService
        .planning(this.filter)
        .then((data) => {
          this.displayYear = false;
          this.planning = data;
          this.removeDuplicate();
          this.canHideLoader = true;
          this.datePlanning = moment(this.filter.date);
        })
        .catch((e) => {})
        .finally(() => {
          this.hideCustomLoader();
          this.$nextTick(() => {
            this.$refs.planning.resetPage();
          });
        });
    },
    getPlanningYearly() {
      this.filter.dateYearly = this.filter.date;
      if (!this.filter.accommodationId) {
        this.filter.accommodationId = this.$route.query.accommodationId;
      }

      reservationPlanningService
        .planningYearly(this.filter)
        .then((data) => {
          this.planning = data;
          this.canHideLoader = true;
          this.datePlanning = moment(this.filter.date);
          this.displayYear = true;
        })
        .catch((e) => {})
        .finally(() => {
          this.hideCustomLoader();
        });
    },

    removeDuplicate() {
      this.planning.sort(function compare(a, b) {
        if (a.ownerDisplay.toLowerCase() < b.ownerDisplay.toLowerCase()) return -1;
        if (a.ownerDisplay.toLowerCase() > b.ownerDisplay.toLowerCase()) return 1;
        return 0;
      });
    },

    // FILTER

    openFilterPanel() {
      this.toggleFilterTitle(
        'GaReservationFilterPlanning',
        this.$t('filter_all'),
        this.filter,
        this.search,
        this.resetFilter,
      );
    },

    // CREATE RESERVATION

    createReservation() {
      let reservationHote = {
        id: undefined,
        accommodationId: this.filter.accommodationId,
        dateStart: undefined,
        dateEnd: undefined,
        hourCheckin: undefined,
        hourCheckout: undefined,
        comment: undefined,
      };
      let disableResaHoteAccommodation = !!this.filter.accommodationId;

      this.$refs.createOwnerReservationSidebar.show(reservationHote, this.accommodations, disableResaHoteAccommodation);
    },

    // EDIT RESERVATION

    editReservation(reservation) {
      let reservationHote = {
        id: reservation.id,
        accommodationId: reservation.accommodationId,
        dateStart: moment(reservation.dateStart).toDate(),
        dateEnd: moment(reservation.dateEnd).toDate(),
        hourCheckin: moment(reservation.dateCheckin).toDate(),
        hourCheckout: moment(reservation.dateCheckout).toDate(),
        comment: reservation.comments,
      };
      let disableResaHoteAccommodation = true;

      this.$refs.createOwnerReservationSidebar.show(reservationHote, this.accommodations, disableResaHoteAccommodation);
    },

    // ADD RESERVATION

    addReservation(dateInfo) {
      if (!this.canAddReservation()) return;

      let year = dateInfo[0];
      let month = dateInfo[1];
      let day = dateInfo[2];

      let start = moment().set({year: year, month: month, date: day});
      if (!start.isAfter(moment())) return;

      let reservationHote = {
        id: undefined,
        accommodationId: this.filter.accommodationId,
        dateStart: start.toDate(),
        dateEnd: undefined,
        hourCheckin: undefined,
        hourCheckout: undefined,
        comment: undefined,
      };
      let disableResaHoteAccommodation = !!this.filter.accommodationId;
      this.$refs.createOwnerReservationSidebar.show(reservationHote, this.accommodations, disableResaHoteAccommodation);
    },

    // CAN

    canAddReservation() {
      if (this.isPartenaire()) return false;
      return true;
    },
  },
};
</script>

<template>
  <GaView v-show="hasAccommodation || !isOwner()" class="pb-18">
    <template v-slot:action>
      <div class="flex gap-2">
        <AkButtonActionV2
          v-if="this.isOwner() || this.isHousekeeper()"
          :label="$t('unavailability.add')"
          @click="createReservation()"></AkButtonActionV2>
        <AkButtonActionV2 v-if="!this.$isMobile()" :label="this.$t('today')" @click="today()"></AkButtonActionV2>
      </div>
    </template>

    <template #content>
      <GaListView>
        <template #tabs>
          <div class="flex items-center md:flex-row flex-row-reverse justify-end md:justify-start">
            <span
              v-for="({label}, index) in this.getDurationsOptions"
              :key="label"
              class="list-table-item"
              :class="durationSelectedId === index && 'active'"
              @click="this.selectDuration(index, true)">
              {{ label }}
            </span>
          </div>
        </template>
        <template #filter>
          <AkListFilter
            v-if="this.isGas()"
            :multi-selection="true"
            v-model="this.filter.companyIds"
            :items="this.companies"
            :display-filter="true"
            @search="this.search"
            :label="$t('employee.company')"
            direction="right" />
          <AkAutocompleteFilter
            v-if="!this.isOwner()"
            v-model="this.filter.ownerId"
            @search="this.search"
            label="Hôte"
            item-value="id"
            item-display="display"
            direction="right"
            :init-method="this.autocompleteOwnerById"
            :search-method="this.autocompleteOwner"
            :search-object="this.autocompleteOwnerCustomer()"
            :search-label="this.autocompleteOwnerQuery" />
          <AkAutocompleteFilter
            v-if="!this.isOwner()"
            v-model="filter.accommodationId"
            :minLengthToLaunchSearch="3"
            :init-method="autocompleteAccommodationById"
            label="Logement"
            :search-label="autocompleteAccommodationQuery"
            :search-method="autocompleteAccommodation"
            direction="right"
            item-display="display"
            item-value="id"
            @search="this.search" />

          <AkListFilter
            v-if="this.isOwner()"
            v-model="filter.accommodationId"
            label="Logement"
            direction="right"
            :multi-selection="false"
            plural-label="Logements"
            :items="this.accommodations"
            item-display="display"
            item-value="id"
            @search="this.search" />
          <AkAllFilter @open="this.openFilterPanel" />
        </template>

        <template #content>
          <div ref="listView">
            <AkPlanning
              v-if="planning && !displayYear"
              ref="planning"
              v-model="planning"
              :date-ref="datePlanning"
              :nb-days-display="nbDays"
              @next="nextDays"
              @delete-reservation="deleteReservation"
              @edit-reservation="editReservation"
              @add-reservation="addReservation"
              @prev="prevDays" />

            <AkPlanningYear
              v-if="planning && displayYear"
              v-model="planning"
              :date-ref="datePlanning"
              @next="nextYear"
              @prev="prevYear"
              @delete-reservation="deleteReservation"
              @edit-reservation="editReservation"
              @add-reservation="addReservation" />
          </div>
        </template>
      </GaListView>
    </template>
    <!----------------------------------------------------------------------------------------->
    <template #extra>
      <CreateOwnerReservationSidebar ref="createOwnerReservationSidebar" @done="reloadPage" />
    </template>
  </GaView>

  <ga-layout-empty-tab
    v-if="!this.hasAccommodation && isOwner()"
    icon="ga-calendar"
    title="Mon __calendrier__"
    gradient="linear-gradient(109deg, rgba(156, 231, 174, 0.20) 1.88%, rgba(211, 252, 221, 0.00) 44.31%), linear-gradient(0deg, #E9FEEE 0%, #E9FEEE 100%)"
    background-color="rgba(233, 254, 238, 1)"
    @init="init">
    <div class="flex flex-col grow items-center md:gap-8 gap-4 md:p-4 p-8 pb-0 md:min-w-[45%]">
      <div
        class="border-[7px] border-[rgba(164,_246,_188,_1)] rounded-full flex items-center justify-center w-[70px] h-[70px]">
        <i class="ga-icon ga-calendar text-[30px] text-grey-900" />
      </div>
      <div class="flex flex-col gap-6 items-center">
        <div class="text-base text-grey-900 text-center">
          <div class="font-semibold text-lg">Retrouvez ici votre calendrier !</div>
          Publiez votre logement sur Guestadom et générez vos premières réservations !
        </div>
        <AkButtonValidation
          ref="onboardingButton"
          class="btn-primary !h-[48px] w-fit !px-8 font-medium"
          @click="goToOnboarding"
          :label="hasPendingOnboarding ? 'Reprendre la publication' : 'Démarrer la publication'" />
      </div>
    </div>
    <img v-if="$isMobile()" src="@refonte/img/emptyTabs/calendrier_mobile_illustration.png" alt="" class="w-full" />
    <div v-else class="h-full md:max-w-[55%] w-full flex items-end justify-end">
      <img src="@refonte/img/emptyTabs/calendrier_illustration.png" alt="" class="h-auto object-cover object-left" />
    </div>
  </ga-layout-empty-tab>
</template>
